import { Fragment } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { joinList } from "../joinList";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { IconButton } from "../primitives/IconButton";
import { SelectMark } from "../primitives/SelectMark";
import type { UiTheme } from "../theme";

/** One row of the day as the wizard shows it: its exercise now, and the
 *  row's own exercises (the first is the default). */
export type WizardRow = { id: string; exercise: string; exercises: readonly string[] };

// One day of the model wizard (#351): its rows at the chosen level and time.
// A row with alternatives opens them under its name, and the pick swaps the
// exercise; any row can be taken out and put back, as in the tetrad wizard.
// #492 F3: a row the fit cut for time shows struck through too, with what it
// leaves under MEV; putting it back keeps it, and the fit cuts another.
export function ModelWizardDay({
  theme,
  name,
  rows,
  removed,
  cut,
  open,
  nameOf,
  onToggleRemove,
  onToggleOpen,
  onPick,
  onLayoutY,
}: {
  theme: UiTheme;
  name: string;
  rows: readonly WizardRow[];
  removed: ReadonlySet<string>;
  /** The rows the fit cut for time, each with the muscles it leaves under
   *  MEV, by name (none: an empty list). */
  cut: ReadonlyMap<string, readonly string[]>;
  /** The row whose alternatives are open, if any. */
  open: string | null;
  nameOf: (slug: string) => string;
  onToggleRemove: (rowId: string) => void;
  onToggleOpen: (rowId: string) => void;
  onPick: (rowId: string, slug: string) => void;
  /** The day's top in its scroll content, so «Create» can bring an empty day into view. */
  onLayoutY?: (y: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  const kept = rows.filter((r) => !removed.has(r.id)).length;
  return (
    <View style={styles.day} onLayout={(e) => onLayoutY?.(e.nativeEvent.layout.y)}>
      <Text style={styles.dayName} accessibilityRole="header">
        {name}
      </Text>
      {kept === 0 ? (
        <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
          {t("editorV3.tetrad.dayEmpty", { defaultValue: "No exercise: the day needs at least one." })}
        </Text>
      ) : null}
      {rows.map((row) => {
        const n = nameOf(row.exercise);
        const uncovered = cut.get(row.id);
        const out = removed.has(row.id) || uncovered !== undefined;
        const cutNote =
          uncovered === undefined
            ? null
            : uncovered.length === 0
              ? t("programModel.wizard.cut.row", { defaultValue: "Cut for time" })
              : t("programModel.wizard.cut.rowUncovered", {
                  count: uncovered.length,
                  muscles: joinList([...uncovered], i18n.language),
                  defaultValue: "Cut for time: {{muscles}} stay under MEV",
                });
        const swappable = row.exercises.length > 1 && !out;
        const expanded = swappable && open === row.id;
        return (
          <Fragment key={row.id}>
            <View style={styles.row}>
              {swappable ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t("editorV3.model.swapA11y", {
                    name: n,
                    count: row.exercises.length - 1,
                    defaultValue: "{{name}}. Swap it for one of {{count}} alternatives",
                  })}
                  accessibilityState={{ expanded }}
                  // RNW drops accessibilityState: this reaches the web.
                  aria-expanded={expanded}
                  onPress={() => onToggleOpen(row.id)}
                  style={({ pressed }) => [styles.swap, pressed && styles.pressed]}
                >
                  <View style={styles.exerciseCol}>
                    <Text style={styles.exerciseText}>{n}</Text>
                    <Text style={styles.hint}>
                      {t("editorV3.model.alternativesCount", { count: row.exercises.length - 1, defaultValue: "{{count}} alternatives" })}
                    </Text>
                  </View>
                  <Icon
                    name={expanded ? "chevron-up" : "chevron-down"}
                    size={theme.typography.sizes.md}
                    color={theme.palette.ink3}
                  />
                </Pressable>
              ) : (
                <Text
                  style={out ? styles.exerciseOut : styles.exercise}
                  accessibilityLabel={
                    cutNote
                      ? `${n}, ${cutNote}`
                      : out
                        ? `${n}, ${t("editorV3.tetrad.takenOut", { defaultValue: "taken out" })}`
                        : undefined
                  }
                >
                  {n}
                </Text>
              )}
              <IconButton
                theme={theme}
                name={out ? "undo" : "x"}
                accessibilityLabel={
                  out
                    ? t("editorV3.tetrad.putBack", { name: n, defaultValue: "Put back {{name}}" })
                    : t("editorV3.tetrad.removeExercise", { name: n, defaultValue: "Take out {{name}}" })
                }
                accessibilityHint={
                  cutNote
                    ? t("programModel.wizard.cut.putBackHint", {
                        defaultValue: "It stays, and another exercise is cut in its place.",
                      })
                    : undefined
                }
                onPress={() => onToggleRemove(row.id)}
              />
            </View>
            {cutNote ? (
              <View style={styles.cutNote} {...hiddenFromScreenReader()}>
                <Icon name="scissors" size={theme.typography.sizes.sm} color={theme.palette.ink3} />
                <Text style={styles.cutText}>{cutNote}</Text>
              </View>
            ) : null}
            {expanded ? (
              <View
                style={styles.choices}
                accessibilityRole="radiogroup"
                accessibilityLabel={t("editorV3.model.alternatives", { defaultValue: "Alternatives" })}
              >
                {row.exercises.map((slug) => {
                  const on = slug === row.exercise;
                  return (
                    <Pressable
                      key={slug}
                      accessibilityRole="radio"
                      accessibilityLabel={nameOf(slug)}
                      accessibilityState={{ checked: on }}
                      aria-checked={on}
                      onPress={() => onPick(row.id, slug)}
                      style={({ pressed }) => [styles.choice, pressed && styles.pressed]}
                    >
                      <SelectMark theme={theme} checked={on} />
                      <Text style={on ? styles.choiceOn : styles.exercise}>{nameOf(slug)}</Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : null}
          </Fragment>
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    day: { gap: s.s1 },
    dayName: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    hint: { color: p.ink3, fontSize: ty.sizes.sm, lineHeight: ty.sizes.sm * 1.4 },
    row: { flexDirection: "row", alignItems: "center", minHeight: 44, gap: s.s3 },
    swap: { flex: 1, flexDirection: "row", alignItems: "center", minHeight: 44, gap: s.s2 },
    pressed: { backgroundColor: p.surface2 },
    exerciseCol: { flex: 1 },
    exerciseText: { color: p.ink, fontSize: ty.sizes.base },
    exercise: { flex: 1, color: p.ink, fontSize: ty.sizes.base },
    // Taken out: struck through and quieter, and the button says «put back».
    exerciseOut: { flex: 1, color: p.ink3, fontSize: ty.sizes.base, textDecorationLine: "line-through" },
    // Said in the row's label already: shown, not read twice.
    cutNote: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    cutText: { flex: 1, color: p.ink3, fontSize: ty.sizes.sm, lineHeight: ty.sizes.sm * 1.4 },
    choices: { gap: s.s1, paddingLeft: s.s4 },
    choice: { flexDirection: "row", alignItems: "center", minHeight: 44, gap: s.s3 },
    choiceOn: { flex: 1, color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
  });
}
